import { useEffect, useRef } from 'react'
import { io } from 'socket.io-client'
import { apiURL } from '../utilities/URLs'
import { account } from '../classes/Account'

/**
 * Connects to the portal Socket.IO server and listens for
 * inbound-sms events targeted at the current application.
 *
 * Shows a DOM toast notification when a new message arrives.
 *
 * @param {Object} params
 * @param {Function} [params.onInboundSMS] - callback with the smsData payload
 */
export default function usePortalSocket({ onInboundSMS } = {}) {
  const toastTimeoutRef = useRef(null)
  const callbackRef = useRef(onInboundSMS)
  callbackRef.current = onInboundSMS

  useEffect(() => {
    if (!account.applicationId) return

    const socket = io(apiURL, {
      transports: ['websocket'],
      upgrade: false
    })

    socket.on('connect', () => {
      socket.emit('join-application-room', account.applicationId)
    })

    socket.on('inbound-sms', (data) => {
      if (callbackRef.current) callbackRef.current(data)
      showToast({
        title: 'New Message',
        name: data.contactName || 'Your Agent',
        subtitle: data.messagePreview || '',
      })
    })

    return () => {
      socket.disconnect()
      dismissToast()
    }
  }, [])

  /* ── Toast rendering ─────────────────────────────────── */

  /* ── Toast rendering ─────────────────────────────────── */

  const TOAST_DURATION = 8000

  const colorMap = {
    'inbound-sms': {
      iconBg: 'rgba(28, 62, 107, 0.1)',
      iconStroke: '#1C3E6B',
      actionBg: '#1C3E6B',
      borderColor: '#1C3E6B',
      icon: `<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>`
    }
  }

  const showToast = ({ title, name, subtitle }) => {
    dismissToast()

    // Default to SMS style for the client portal
    const colors = colorMap['inbound-sms']

    const toast = document.createElement('div')
    toast.id = 'portal-realtime-toast'
    // Cleaner, white-theme card style matching the Admin Portal UI
    toast.style.cssText = `
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 9999;
      background: #ffffff;
      color: #1f2937;
      padding: 16px;
      border-radius: 8px;
      border-left: 5px solid ${colors.borderColor};
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
      cursor: pointer;
      display: flex;
      align-items: flex-start;
      gap: 16px;
      min-width: 320px;
      max-width: 400px;
      font-family: 'Poppins', sans-serif;
      animation: slideInPortalToast 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      transition: all 0.2s ease;
    `

    toast.innerHTML = `
      <div style="
        width: 36px; height: 36px; border-radius: 50%;
        background: ${colors.iconBg}; display: flex;
        align-items: center; justify-content: center; flex-shrink: 0;
        margin-top: 2px;
      ">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${colors.iconStroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          ${colors.icon}
        </svg>
      </div>
      <div style="flex: 1; min-width: 0;">
        <h4 style="margin: 0; font-weight: 600; font-size: 14px; color: #111827; line-height: 1.4;">${title}</h4>
        <p style="margin: 4px 0 0; font-weight: 500; font-size: 13px; color: #4B5563; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${name}</p>
        ${subtitle ? `<p style="margin: 2px 0 0; font-size: 12px; color: #6B7280; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">${subtitle}</p>` : ''}
      </div>
      <button style="
        background: ${colors.actionBg};
        color: white;
        border: none;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        align-self: center;
        transition: opacity 0.2s;
        font-family: inherit;
      " onmouseover="this.style.opacity='0.9'" onmouseout="this.style.opacity='1'">
        View
      </button>
    `

    if (!document.getElementById('portal-toast-keyframes')) {
      const style = document.createElement('style')
      style.id = 'portal-toast-keyframes'
      style.textContent = `
        @keyframes slideInPortalToast {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
      `
      document.head.appendChild(style)
    }

    // click to dismiss
    toast.addEventListener('click', () => {
      // Logic to open chat could go here if we had access to the router context easily, 
      // but for now it just dismisses since they are likely already on the dashboard.
      dismissToast()
    })

    document.body.appendChild(toast)

    toastTimeoutRef.current = setTimeout(() => {
      dismissToast()
    }, TOAST_DURATION)
  }

  const dismissToast = () => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current)
      toastTimeoutRef.current = null
    }
    const existing = document.getElementById('portal-realtime-toast')
    if (existing) {
      existing.style.opacity = '0'
      existing.style.transform = 'translateY(20px)'
      setTimeout(() => existing.remove(), 250)
    }
  }
}
